import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import {
  Mail, MessageSquare, Plus, X, RefreshCw, Trash2, ChevronUp, ChevronDown,
  Zap, ShieldOff, Send, AlertCircle, CheckCircle2, Clock, PauseCircle,
} from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

// ============================================================================
// Recovery Sequences Admin (Phase 4) — 3 tabs (Sequences | Templates |
// Enrollment Log), merge-field picker, SMS segment counter, step reorder.
// Every mutation reads/writes /api/admin/recovery/*; tenant is selected via
// dropdown so a platform_admin can operate across every tenant without
// leaving the page. RECOVERY_AUTOSEND_ENABLED status is surfaced at the top
// so the admin sees the master safety switch's state at all times.
// ============================================================================

const CHANNELS = [
  { value: "email", label: "Email", icon: Mail, color: "#0072bc" },
  { value: "sms",   label: "SMS",   icon: MessageSquare, color: "#0e8a68" },
];
const CHANNEL_MAP = Object.fromEntries(CHANNELS.map((c) => [c.value, c]));

const TRIGGER_BUCKETS = [
  "HARD_CANCEL", "SOFT_CANCEL", "RESCHEDULE", "NO_SHOW", "OTHER",
];

// Aligned with dispatcher.py MERGE_FIELDS. Any change here must ship with a
// dispatcher-side change too — kept as a hardcoded constant on purpose to
// force a code-review couple when adding new tokens.
const MERGE_FIELDS = [
  { token: "{{customer_first_name}}", label: "Customer first name",  sample: "Jamie" },
  { token: "{{customer_full_name}}",  label: "Customer full name",   sample: "Jamie Rivera" },
  { token: "{{job_number}}",           label: "Job number",           sample: "JOB-1042" },
  { token: "{{cancel_date}}",          label: "Cancellation date",    sample: "2026-07-23" },
  { token: "{{tenant_name}}",          label: "Tenant / franchise name", sample: "MRE SWMO" },
  { token: "{{recovery_link}}",        label: "Recovery link (Phase 5)", sample: "https://…" },
];

const ENROLLMENT_STATUSES = ["active", "paused_no_date", "paused_no_contact_info", "completed", "opted_out"];
const ENROLLMENT_TONE = {
  active: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]",
  paused_no_date: "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]",
  paused_no_contact_info: "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]",
  completed: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
  opted_out: "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Light-mode input styling shared across all admin modals in this page (the
// index.css `.cc-input` is dark-navy — designed for dashboard forms, not the
// white modal chrome we use for platform_admin flows).
const INPUT_CLS = "w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#0072bc]";

// GSM-7 default alphabet detection — mixed-Unicode SMS gets segmented at 70
// chars/segment instead of 160, so a single emoji or curly quote in a
// template can HALF the deliverable body length. We tell the admin explicitly.
const GSM7_CHARS = new Set(
  "@£$¥èéùìòÇ\nØø\rÅå_ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"
    .split("")
);
const GSM7_EXT = new Set("^{}\\[~]|€".split(""));

function smsSegmentInfo(body) {
  // Merge-field placeholders are treated as their sample rendered length so
  // the count reflects the WORST-CASE customer-facing length, not the
  // template string length. This is what actually gets billed.
  let rendered = body;
  for (const m of MERGE_FIELDS) rendered = rendered.split(m.token).join(m.sample);
  const chars = [...rendered];
  const isGsm7 = chars.every((c) => GSM7_CHARS.has(c) || GSM7_EXT.has(c));
  const gsmLen = isGsm7
    ? chars.reduce((n, c) => n + (GSM7_EXT.has(c) ? 2 : 1), 0)
    : chars.length;
  const perSegment = isGsm7 ? (gsmLen > 160 ? 153 : 160) : (gsmLen > 70 ? 67 : 70);
  const segments = Math.max(1, Math.ceil(gsmLen / perSegment));
  return { chars: gsmLen, segments, encoding: isGsm7 ? "GSM-7" : "UCS-2", perSegment };
}

// ============================================================================
// Root
// ============================================================================
export default function AdminRecoverySequences() {
  const [tenants, setTenants] = useState([]);
  const [tenantSlug, setTenantSlug] = useState("");
  const [tenantsLoad, setTenantsLoad] = useState({ status: "loading", error: null });

  // Provider config (Phase 3 gate — visible at all times so the admin never
  // confuses "sequence active" with "autosend on").
  const [config, setConfig] = useState(null);
  const [configLoad, setConfigLoad] = useState({ status: "loading", error: null });

  // Scheduler status (Phase 5.5 — in-process worker state snapshot).
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerLoad, setSchedulerLoad] = useState({ status: "loading", error: null });

  const [tab, setTab] = useState("sequences");

  const loadTenants = useCallback(async () => {
    setTenantsLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/tenants");
      const rows = (Array.isArray(data) ? data : []).filter((t) => t.slug);
      setTenants(rows);
      setTenantsLoad({ status: "ok", error: null });
      if (!tenantSlug && rows.length) setTenantSlug(rows[0].slug);
    } catch (err) {
      setTenants([]);
      setTenantsLoad({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug]);

  const loadConfig = useCallback(async () => {
    setConfigLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/admin/recovery/config");
      setConfig(data);
      setConfigLoad({ status: "ok", error: null });
    } catch (err) {
      setConfig(null);
      setConfigLoad({ status: "error", error: extractApiError(err) });
    }
  }, []);

  const loadSchedulerStatus = useCallback(async () => {
    setSchedulerLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/admin/recovery/scheduler-status");
      setSchedulerStatus(data);
      setSchedulerLoad({ status: "ok", error: null });
    } catch (err) {
      setSchedulerStatus(null);
      setSchedulerLoad({ status: "error", error: extractApiError(err) });
    }
  }, []);

  useEffect(() => { loadTenants(); loadConfig(); loadSchedulerStatus(); },
    [loadTenants, loadConfig, loadSchedulerStatus]);

  // Auto-refresh scheduler status every 30s while page is open — cheap
  // (in-memory snapshot, zero DB round-trips server-side).
  useEffect(() => {
    const id = setInterval(loadSchedulerStatus, 30000);
    return () => clearInterval(id);
  }, [loadSchedulerStatus]);

  return (
    <div data-testid="admin-recovery-page" className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            Platform admin
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
            <Send size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Recovery Sequences
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Automated post-cancellation nurture. Sequences → Templates → Enrollment log.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
            Tenant
          </label>
          <select
            data-testid="recovery-tenant-select"
            value={tenantSlug}
            onChange={(e) => setTenantSlug(e.target.value)}
            className="border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] bg-white outline-none focus:border-[#0072bc] min-w-[220px]"
          >
            {tenants.map((t) => (
              <option key={t.slug} value={t.slug}>{t.business_name} · /{t.slug}</option>
            ))}
          </select>
        </div>
      </header>

      {/* Autosend safety banner — visible on every tab. */}
      <AutosendBanner config={config} loadState={configLoad} onRefresh={loadConfig} />

      {/* Phase 5.5 scheduler status — dry-run previews before go-live. */}
      <SchedulerStatusBanner
        status={schedulerStatus}
        loadState={schedulerLoad}
        onRefresh={loadSchedulerStatus}
      />

      {/* Tab switcher */}
      <div className="border-b border-[#e7e9ee] flex items-center gap-1" data-testid="recovery-tabs">
        {[
          { key: "sequences", label: "Sequences" },
          { key: "templates", label: "Templates" },
          { key: "enrollments", label: "Enrollment Log" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`recovery-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[#0072bc] text-[#0072bc]"
                : "border-transparent text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!tenantSlug ? (
        <PanelState status={tenantsLoad.status} error={tenantsLoad.error}
          isEmpty={tenants.length === 0} emptyTitle="No tenants available"
          emptyBody="Provision a tenant first." testIdPrefix="recovery-tenants" />
      ) : tab === "sequences" ? (
        <SequencesTab tenantSlug={tenantSlug} />
      ) : tab === "templates" ? (
        <TemplatesTab tenantSlug={tenantSlug} />
      ) : (
        <EnrollmentsTab tenantSlug={tenantSlug} />
      )}
    </div>
  );
}

// ============================================================================
// Autosend banner
// ============================================================================
function AutosendBanner({ config, loadState, onRefresh }) {
  if (loadState.status === "loading") return null;
  if (loadState.status === "error") {
    return (
      <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c] flex items-center justify-between gap-3"
        data-testid="autosend-banner-error">
        <span>Could not load provider config: {loadState.error?.message}</span>
        <button onClick={onRefresh} className="text-[11.5px] font-semibold underline">Retry</button>
      </div>
    );
  }
  const mode = config?.effective_mode || "off";
  const needed = config?.needed_channels || [];
  const missing = config?.missing_channels || [];
  const tone = mode === "off"
    ? "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"
    : mode === "mock"
    ? "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]"
    : "bg-[#fdecec] text-[#7a1c1c] border-[#f4c9c9]";
  const Icon = mode === "off" ? ShieldOff : mode === "mock" ? Clock : Zap;
  const headline = mode === "off"
    ? "Autosend is OFF — no sends will fire (even in mock form)."
    : mode === "mock"
    ? `Autosend is ON but running in MOCK mode — active sequences need ${missing.join(" + ")}, provider(s) not configured; MessageSendLog rows are marked mocked=true.`
    : `Autosend is LIVE — real customer messages will be sent when a step fires (channels: ${needed.join(", ") || "email"}).`;
  return (
    <div className={`border rounded-lg p-3.5 flex items-start gap-3 ${tone}`}
      data-testid={`autosend-banner-${mode}`}>
      <Icon size={18} strokeWidth={1.8} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold">{headline}</div>
        <div className="text-[11.5px] font-mono-data mt-1 opacity-90">
          RECOVERY_AUTOSEND_ENABLED={String(!!config?.autosend_enabled)} ·
          twilio_configured={String(!!config?.twilio_configured)} ·
          resend_configured={String(!!config?.resend_configured)}
        </div>
      </div>
      <button onClick={onRefresh} className="p-1.5 rounded-md hover:bg-white/60"
        data-testid="autosend-banner-refresh" aria-label="Refresh provider config">
        <RefreshCw size={14} strokeWidth={1.8} />
      </button>
    </div>
  );
}

// ============================================================================
// Scheduler status banner (Phase 5.5) — surfaces the in-process asyncio
// worker's live state. When autosend is OFF, the scheduler still ticks and
// records "dry-run previews" of what WOULD dispatch. That's the primary
// pre-flight validation surface before flipping RECOVERY_AUTOSEND_ENABLED.
// Auto-refreshes every 30s via the parent component.
// ============================================================================
function SchedulerStatusBanner({ status, loadState, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  if (loadState.status === "loading" && !status) return null;
  if (loadState.status === "error") {
    return (
      <div className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c] flex items-center justify-between gap-3"
        data-testid="scheduler-banner-error">
        <span>Could not load scheduler status: {loadState.error?.message}</span>
        <button onClick={onRefresh} className="text-[11.5px] font-semibold underline">Retry</button>
      </div>
    );
  }
  if (!status) return null;
  const previews = status.dry_run_previews_recent || [];
  const inDryRun = !status.autosend_enabled;
  const tone = inDryRun
    ? "bg-[#eef4ff] text-[#1c3a75] border-[#c7d6f2]"
    : "bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]";
  const lastTickRel = status.last_tick_at
    ? new Date(status.last_tick_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : "never";
  return (
    <div className={`border rounded-lg p-3.5 ${tone}`} data-testid="scheduler-status-banner">
      <div className="flex items-start gap-3">
        <Clock size={18} strokeWidth={1.8} className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold flex items-center gap-2 flex-wrap">
            <span>
              Scheduler {inDryRun ? "in DRY-RUN" : "LIVE"}
            </span>
            <span className="text-[11.5px] opacity-75">
              · tick every {status.tick_seconds}s · window {status.send_window}
            </span>
          </div>
          <div className="text-[11.5px] font-mono-data mt-1 opacity-90 flex flex-wrap gap-x-4 gap-y-1">
            <span>last_tick={lastTickRel}</span>
            <span>ticks={status.ticks_total}</span>
            <span>dispatched={status.dispatched_total}</span>
            <span>held_by_window={status.held_by_window_total}</span>
            <span>retried={status.retried_total}</span>
            <span>tick_ms={status.last_tick_took_ms}</span>
          </div>
          {inDryRun && (
            <div className="text-[11.5px] mt-1.5 opacity-90">
              Dry-run previews collected: <span className="font-semibold">{previews.length}</span>
              {previews.length > 0 && (
                <button
                  onClick={() => setExpanded((x) => !x)}
                  className="ml-2 text-[11px] font-semibold underline"
                  data-testid="scheduler-previews-toggle"
                >
                  {expanded ? "hide" : "show"} recent
                </button>
              )}
            </div>
          )}
        </div>
        <button onClick={onRefresh} className="p-1.5 rounded-md hover:bg-white/60"
          data-testid="scheduler-banner-refresh" aria-label="Refresh scheduler status">
          <RefreshCw size={14} strokeWidth={1.8} />
        </button>
      </div>
      {inDryRun && expanded && previews.length > 0 && (
        <div className="mt-3 pt-3 border-t border-current/20" data-testid="scheduler-previews-list">
          <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-70 mb-1.5">
            Recent dry-run previews (would dispatch if autosend were ON)
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {previews.slice().reverse().map((p, idx) => (
              <div key={idx} className="text-[11px] font-mono-data flex flex-wrap gap-x-3 gap-y-0.5 py-0.5 border-b border-current/10 last:border-0">
                <span>{new Date(p.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                <span>ch={p.channel}</span>
                <span>step={p.step_order}</span>
                <span>delay={p.delay_days}d</span>
                <span className="truncate">{p.customer_ref}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sequences tab
// ============================================================================
function SequencesTab({ tenantSlug }) {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [expanded, setExpanded] = useState(null); // sequence id
  const [addingSeq, setAddingSeq] = useState(false);
  const [addingStepTo, setAddingStepTo] = useState(null); // sequence id
  const [busyIds, setBusyIds] = useState(new Set());

  const busy = (id) => busyIds.has(id);
  const withBusy = async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try { return await fn(); } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const load = useCallback(async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const [seqRes, tplRes] = await Promise.all([
        api.get("/admin/recovery/sequences", { params: { tenant_slug: tenantSlug } }),
        api.get("/admin/recovery/templates",  { params: { tenant_slug: tenantSlug } }),
      ]);
      setSequences(Array.isArray(seqRes.data) ? seqRes.data : []);
      setTemplates(Array.isArray(tplRes.data) ? tplRes.data : []);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = (seq) => withBusy(seq.id, async () => {
    const next = !seq.active;
    if (next && !window.confirm(
      `Set "${seq.name}" to ACTIVE?\n\n` +
      "New cancellations matching trigger_bucket will start generating enrollments. " +
      "The autosend flag is a SEPARATE gate — sends still require RECOVERY_AUTOSEND_ENABLED=true.\n\n" +
      "Confirm you have vetted the data feed?"
    )) return;
    try {
      const { data } = await api.patch(`/admin/recovery/sequences/${seq.id}`,
        { active: next }, { params: { tenant_slug: tenantSlug } });
      setSequences((rows) => rows.map((r) => r.id === seq.id ? { ...r, ...data } : r));
    } catch (err) {
      window.alert(extractApiError(err).message || "Toggle failed.");
    }
  });

  const deleteSeq = (seq) => withBusy(seq.id, async () => {
    if (!window.confirm(`Delete sequence "${seq.name}" and all its steps? This is irreversible.`)) return;
    try {
      await api.delete(`/admin/recovery/sequences/${seq.id}`, { params: { tenant_slug: tenantSlug } });
      setSequences((rows) => rows.filter((r) => r.id !== seq.id));
    } catch (err) {
      window.alert(extractApiError(err).message || "Delete failed.");
    }
  });

  const reorder = async (seq, stepIndex, dir) => {
    const newIndex = stepIndex + dir;
    if (newIndex < 0 || newIndex >= seq.step_details.length) return;
    const ids = seq.step_details.map((s) => s.id);
    [ids[stepIndex], ids[newIndex]] = [ids[newIndex], ids[stepIndex]];
    try {
      await api.post(`/admin/recovery/sequences/${seq.id}/steps/reorder`,
        { step_ids: ids }, { params: { tenant_slug: tenantSlug } });
      await load();
    } catch (err) {
      window.alert(extractApiError(err).message || "Reorder failed.");
    }
  };

  const deleteStep = async (seq, step) => {
    if (!window.confirm(`Remove step ${step.order} from "${seq.name}"?`)) return;
    try {
      await api.delete(`/admin/recovery/sequences/${seq.id}/steps/${step.id}`,
        { params: { tenant_slug: tenantSlug } });
      await load();
    } catch (err) {
      window.alert(extractApiError(err).message || "Delete step failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <button
          data-testid="add-sequence-btn"
          onClick={() => setAddingSeq(true)}
          className="cc-btn-primary text-[13px]"
        >
          <Plus size={15} strokeWidth={1.8} /> New sequence
        </button>
        <button onClick={load} data-testid="sequences-refresh"
          className="text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8] inline-flex items-center gap-1.5">
          <RefreshCw size={13} strokeWidth={1.8} /> Refresh
        </button>
      </div>

      <PanelState
        status={loadState.status} error={loadState.error}
        isEmpty={sequences.length === 0} onRetry={load}
        emptyTitle="No sequences configured yet"
        emptyBody='Click "New sequence" to build the first recovery flow.'
        testIdPrefix="sequences"
      />

      {loadState.status === "ok" && sequences.map((seq) => (
        <SequenceCard key={seq.id} seq={seq} templates={templates}
          expanded={expanded === seq.id}
          onExpand={() => setExpanded(expanded === seq.id ? null : seq.id)}
          onToggle={() => toggleActive(seq)}
          onDelete={() => deleteSeq(seq)}
          onAddStep={() => setAddingStepTo(seq.id)}
          onReorder={(idx, dir) => reorder(seq, idx, dir)}
          onDeleteStep={(step) => deleteStep(seq, step)}
          busy={busy(seq.id)}
        />
      ))}

      {addingSeq && (
        <SequenceFormModal tenantSlug={tenantSlug}
          onClose={() => setAddingSeq(false)}
          onSaved={() => { setAddingSeq(false); load(); }} />
      )}
      {addingStepTo && (
        <StepFormModal tenantSlug={tenantSlug} sequenceId={addingStepTo}
          templates={templates} existingSteps={
            sequences.find((s) => s.id === addingStepTo)?.step_details || []
          }
          onClose={() => setAddingStepTo(null)}
          onSaved={() => { setAddingStepTo(null); load(); }} />
      )}
    </div>
  );
}

function SequenceCard({ seq, templates, expanded, onExpand, onToggle, onDelete, onAddStep, onReorder, onDeleteStep, busy }) {
  const tplMap = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden"
      data-testid={`sequence-card-${seq.id}`}>
      <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
        <button onClick={onExpand} className="text-left flex-1 min-w-0"
          data-testid={`sequence-toggle-${seq.id}`}>
          <div className="flex items-center gap-2.5">
            <span className="font-display font-semibold text-[15px] text-[#0e1420]">{seq.name}</span>
            <span className={`text-[10.5px] font-mono-data uppercase tracking-widest rounded px-2 py-0.5 border ${
              seq.active
                ? "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]"
                : "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"
            }`} data-testid={`sequence-active-badge-${seq.id}`}>
              {seq.active ? "ACTIVE" : "INACTIVE"}
            </span>
            <span className="text-[10.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-2 py-0.5">
              {seq.trigger_bucket}
            </span>
          </div>
          <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1 font-mono-data">
            {seq.step_details?.length ?? 0} step(s) ·
            {" "}{seq.enrollment_count ?? 0} enrollment(s) ·
            {" "}{seq.completed_count ?? 0} completed
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={onToggle} disabled={busy}
            data-testid={`sequence-active-toggle-${seq.id}`}
            className={`text-[11.5px] font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50 ${
              seq.active
                ? "bg-white text-[#7a1c1c] border-[#f5c2c2] hover:bg-[#fff5f5]"
                : "bg-white text-[#0e5a3f] border-[#c1eddc] hover:bg-[#e6f9f2]"
            }`}>
            {busy ? "…" : seq.active ? "Pause" : "Activate"}
          </button>
          <button onClick={onDelete} disabled={busy}
            data-testid={`sequence-delete-${seq.id}`}
            className="p-1.5 rounded-md text-[color:var(--cc-ink-muted)] hover:text-[#b3261e] hover:bg-[#fff0f0]">
            <Trash2 size={15} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#eef1f6] p-4 bg-[#fafbfc]" data-testid={`sequence-steps-${seq.id}`}>
          {(seq.step_details || []).length === 0 ? (
            <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] italic">No steps yet.</div>
          ) : (
            <ol className="space-y-2">
              {seq.step_details.map((step, idx) => {
                const tpl = tplMap[step.template_id];
                const ch = CHANNEL_MAP[step.channel];
                const Icon = ch?.icon || Mail;
                return (
                  <li key={step.id} data-testid={`step-row-${step.id}`}
                    className="flex items-center gap-3 bg-white border border-[#e7e9ee] rounded-lg p-3">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => onReorder(idx, -1)} disabled={idx === 0}
                        data-testid={`step-move-up-${step.id}`}
                        className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] disabled:opacity-30">
                        <ChevronUp size={14} strokeWidth={1.8} />
                      </button>
                      <button onClick={() => onReorder(idx, +1)} disabled={idx === seq.step_details.length - 1}
                        data-testid={`step-move-down-${step.id}`}
                        className="text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] disabled:opacity-30">
                        <ChevronDown size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                    <div className="text-[13px] font-mono-data text-[color:var(--cc-ink-muted)] w-8">
                      #{step.order}
                    </div>
                    <div className="text-[11.5px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)] whitespace-nowrap">
                      +{step.delay_days}d
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded px-2 py-0.5"
                      style={{ color: ch?.color, background: `${ch?.color}15` }}>
                      <Icon size={12} strokeWidth={1.8} /> {ch?.label}
                    </span>
                    <div className="flex-1 min-w-0 text-[13px] text-[#0e1420] truncate">
                      {tpl ? tpl.name : (
                        <span className="text-[#b3261e]">⚠ template missing ({step.template_id.slice(0, 8)})</span>
                      )}
                    </div>
                    <button onClick={() => onDeleteStep(step)}
                      data-testid={`step-delete-${step.id}`}
                      className="p-1.5 rounded-md text-[color:var(--cc-ink-muted)] hover:text-[#b3261e] hover:bg-[#fff0f0]">
                      <Trash2 size={14} strokeWidth={1.8} />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="mt-3">
            <button onClick={onAddStep}
              data-testid={`add-step-to-${seq.id}`}
              className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] hover:bg-white inline-flex items-center gap-1.5">
              <Plus size={13} strokeWidth={1.8} /> Add step
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SequenceFormModal({ tenantSlug, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", trigger_bucket: "HARD_CANCEL", active: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/admin/recovery/sequences", form,
        { params: { tenant_slug: tenantSlug } });
      onSaved();
    } catch (ex) { setErr(extractApiError(ex)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New sequence" onClose={onClose} testId="add-sequence-modal">
      <form onSubmit={submit} className="p-5 space-y-3">
        <Field label="Name" required>
          <input required value={form.name} data-testid="sequence-form-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={INPUT_CLS} placeholder='e.g. "HVAC Hard-Cancel Nurture"' />
        </Field>
        <Field label="Trigger bucket">
          <select value={form.trigger_bucket} data-testid="sequence-form-bucket"
            onChange={(e) => setForm({ ...form, trigger_bucket: e.target.value })}
            className={INPUT_CLS + " bg-white"}>
            {TRIGGER_BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
        <label className="flex items-start gap-2 text-[12.5px] text-[#0e1420]">
          <input type="checkbox" checked={form.active} data-testid="sequence-form-active"
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="mt-0.5" />
          <span>Activate immediately (start enrolling new cancellations). Recommended: leave OFF and activate from the list after adding steps.</span>
        </label>
        {err && <ErrorInline err={err} testId="sequence-form-error" />}
        <SubmitButtons busy={busy} onClose={onClose} testIdPrefix="sequence-form" label="Create sequence" />
      </form>
    </ModalShell>
  );
}

function StepFormModal({ tenantSlug, sequenceId, templates, existingSteps, onClose, onSaved }) {
  const nextOrder = (existingSteps?.length || 0) + 1;
  const [form, setForm] = useState({
    order: nextOrder, delay_days: 0, channel: "email", template_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const availableTemplates = templates.filter((t) => t.channel === form.channel && t.active);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.template_id) { setErr({ message: "Pick a template." }); return; }
    setBusy(true); setErr(null);
    try {
      await api.post(`/admin/recovery/sequences/${sequenceId}/steps`, form,
        { params: { tenant_slug: tenantSlug } });
      onSaved();
    } catch (ex) { setErr(extractApiError(ex)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add step" onClose={onClose} testId="add-step-modal">
      <form onSubmit={submit} className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Order">
            <input type="number" min={1} required value={form.order}
              data-testid="step-form-order"
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className={INPUT_CLS} />
          </Field>
          <Field label="Delay (days)">
            <input type="number" min={0} required value={form.delay_days}
              data-testid="step-form-delay"
              onChange={(e) => setForm({ ...form, delay_days: Number(e.target.value) })}
              className={INPUT_CLS} />
          </Field>
        </div>
        <Field label="Channel">
          <select value={form.channel} data-testid="step-form-channel"
            onChange={(e) => setForm({ ...form, channel: e.target.value, template_id: "" })}
            className={INPUT_CLS + " bg-white"}>
            {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Template" required>
          {availableTemplates.length === 0 ? (
            <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] italic border border-dashed border-[#e7e9ee] rounded-lg p-3">
              No active {form.channel} templates exist. Create one in the Templates tab first.
            </div>
          ) : (
            <select value={form.template_id} required data-testid="step-form-template"
              onChange={(e) => setForm({ ...form, template_id: e.target.value })}
              className={INPUT_CLS + " bg-white"}>
              <option value="">— select template —</option>
              {availableTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          )}
        </Field>
        {err && <ErrorInline err={err} testId="step-form-error" />}
        <SubmitButtons busy={busy} onClose={onClose} testIdPrefix="step-form" label="Add step" />
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Templates tab
// ============================================================================
function TemplatesTab({ tenantSlug }) {
  const [templates, setTemplates] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [editing, setEditing] = useState(null); // template object OR "new"
  const load = useCallback(async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const { data } = await api.get("/admin/recovery/templates",
        { params: { tenant_slug: tenantSlug } });
      setTemplates(Array.isArray(data) ? data : []);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug]);
  useEffect(() => { load(); }, [load]);

  // Sort by the `dayN_` numeric prefix in the template name so the tab reads
  // in send-day order (day0 → day7 → day30 → …) rather than lexicographic
  // (day0, day1095, day180, day30 …). Non-conforming names fall to the end.
  const sortedTemplates = useMemo(() => {
    const dayNum = (name) => {
      const m = /^day(\d+)_/i.exec(name || "");
      return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    return [...templates].sort((a, b) => {
      const da = dayNum(a.name);
      const db = dayNum(b.name);
      if (da !== db) return da - db;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [templates]);

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/recovery/templates/${t.id}`,
        { params: { tenant_slug: tenantSlug } });
      await load();
    } catch (err) {
      window.alert(extractApiError(err).message || "Delete failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <button data-testid="add-template-btn" onClick={() => setEditing("new")}
          className="cc-btn-primary text-[13px]">
          <Plus size={15} strokeWidth={1.8} /> New template
        </button>
        <button onClick={load} data-testid="templates-refresh"
          className="text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8] inline-flex items-center gap-1.5">
          <RefreshCw size={13} strokeWidth={1.8} /> Refresh
        </button>
      </div>
      <PanelState status={loadState.status} error={loadState.error}
        isEmpty={templates.length === 0} onRetry={load}
        emptyTitle="No templates yet" emptyBody="Create your first email or SMS template."
        testIdPrefix="templates" />
      {loadState.status === "ok" && sortedTemplates.map((t) => {
        const ch = CHANNEL_MAP[t.channel]; const Icon = ch?.icon || Mail;
        const seg = t.channel === "sms" ? smsSegmentInfo(t.body) : null;
        return (
          <div key={t.id} className="bg-white border border-[#e7e9ee] rounded-xl p-4"
            data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold rounded px-2 py-0.5"
                    style={{ color: ch?.color, background: `${ch?.color}15` }}>
                    <Icon size={12} strokeWidth={1.8} /> {ch?.label}
                  </span>
                  <span className="font-display font-semibold text-[14.5px] text-[#0e1420]">{t.name}</span>
                  {!t.active && (
                    <span className="text-[10px] uppercase tracking-widest bg-[#f5f6f8] text-[#4b5563] border border-[#e7e9ee] rounded px-1.5 py-0.5">
                      inactive
                    </span>
                  )}
                </div>
                {t.subject && (
                  <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1">
                    <b>Subject:</b> {t.subject}
                  </div>
                )}
                <div className="text-[12.5px] mt-1.5 whitespace-pre-wrap text-[#0e1420] line-clamp-3">
                  {t.body}
                </div>
                {seg && (
                  <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mt-2"
                    data-testid={`template-sms-info-${t.id}`}>
                    {seg.chars} chars · {seg.encoding} · {seg.segments} segment{seg.segments === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(t)}
                  data-testid={`template-edit-${t.id}`}
                  className="text-[11.5px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8]">
                  Edit
                </button>
                <button onClick={() => del(t)}
                  data-testid={`template-delete-${t.id}`}
                  className="p-1.5 rounded-md text-[color:var(--cc-ink-muted)] hover:text-[#b3261e] hover:bg-[#fff0f0]">
                  <Trash2 size={14} strokeWidth={1.8} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {editing && (
        <TemplateFormModal tenantSlug={tenantSlug}
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function TemplateFormModal({ tenantSlug, template, onClose, onSaved }) {
  const isEdit = Boolean(template);
  const [form, setForm] = useState(template || {
    name: "", channel: "email", subject: "", body: "", active: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const bodyRef = (typeof window !== "undefined") ? { current: null } : null;
  const insertMerge = (token) => {
    // Naive insert: append to body. If we had access to the textarea ref we
    // could insert at cursor; append is fine for now — admins usually copy.
    setForm((f) => ({ ...f, body: `${f.body || ""}${token}` }));
  };
  const seg = form.channel === "sms" ? smsSegmentInfo(form.body || "") : null;
  const submit = async (e) => {
    e.preventDefault();
    if (form.channel === "email" && !form.subject) {
      setErr({ message: "Email templates need a subject." }); return;
    }
    setBusy(true); setErr(null);
    try {
      if (isEdit) {
        await api.patch(`/admin/recovery/templates/${template.id}`,
          { name: form.name, subject: form.subject, body: form.body, active: form.active },
          { params: { tenant_slug: tenantSlug } });
      } else {
        await api.post("/admin/recovery/templates", {
          name: form.name, channel: form.channel,
          subject: form.subject || null, body: form.body,
          merge_fields_used: MERGE_FIELDS
            .filter((m) => (form.body || "").includes(m.token)).map((m) => m.token),
        }, { params: { tenant_slug: tenantSlug } });
      }
      onSaved();
    } catch (ex) { setErr(extractApiError(ex)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title={isEdit ? "Edit template" : "New template"} onClose={onClose}
      testId="template-form-modal">
      <form onSubmit={submit} className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
        <Field label="Name" required>
          <input required value={form.name} data-testid="template-form-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={INPUT_CLS} />
        </Field>
        <Field label="Channel">
          <select value={form.channel} disabled={isEdit}
            data-testid="template-form-channel"
            onChange={(e) => setForm({ ...form, channel: e.target.value })}
            className={INPUT_CLS + " bg-white disabled:opacity-60"}>
            {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        {form.channel === "email" && (
          <Field label="Subject" required>
            <input required value={form.subject || ""}
              data-testid="template-form-subject"
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={INPUT_CLS} />
          </Field>
        )}
        <Field label="Body">
          <textarea rows={6} required value={form.body || ""}
            data-testid="template-form-body" ref={bodyRef}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className={INPUT_CLS + " font-mono-data text-[13px] leading-relaxed"} />
          {seg && (
            <div className={`text-[11px] font-mono-data mt-1.5 ${
              seg.encoding === "UCS-2" ? "text-[#8a5b00]" : "text-[color:var(--cc-ink-muted)]"
            }`} data-testid="template-form-sms-info">
              {seg.chars} chars (rendered) · {seg.encoding} · {seg.segments} segment{seg.segments === 1 ? "" : "s"}
              {seg.encoding === "UCS-2" && " · non-GSM7 chars detected — halves per-segment length"}
            </div>
          )}
        </Field>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
            Merge fields
          </div>
          <div className="flex flex-wrap gap-1.5">
            {MERGE_FIELDS.map((m) => (
              <button key={m.token} type="button"
                data-testid={`merge-field-${m.token.replace(/[{}]/g, "")}`}
                onClick={() => insertMerge(m.token)}
                className="text-[11px] font-mono-data px-2 py-1 rounded-md border border-[#e7e9ee] hover:border-[#0072bc] hover:text-[#0072bc]"
                title={`Renders as: ${m.sample}`}>
                {m.token}
              </button>
            ))}
          </div>
        </div>

        {isEdit && (
          <label className="flex items-center gap-2 text-[12.5px] text-[#0e1420]">
            <input type="checkbox" checked={form.active}
              data-testid="template-form-active"
              onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
        )}
        {err && <ErrorInline err={err} testId="template-form-error" />}
        <SubmitButtons busy={busy} onClose={onClose} testIdPrefix="template-form"
          label={isEdit ? "Save changes" : "Create template"} />
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Enrollments tab
// ============================================================================
function EnrollmentsTab({ tenantSlug }) {
  const [rows, setRows] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [status, setStatus] = useState(""); // filter
  const [expanded, setExpanded] = useState(null); // enrollment id
  const [logs, setLogs] = useState({}); // {enrollment_id: [...]}
  const load = useCallback(async () => {
    setLoadState({ status: "loading", error: null });
    try {
      const params = { tenant_slug: tenantSlug };
      if (status) params.status = status;
      const { data } = await api.get("/admin/recovery/enrollments", { params });
      setRows(Array.isArray(data) ? data : []);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLoadState({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug, status]);
  useEffect(() => { load(); }, [load]);

  const loadLogsFor = async (enrollmentId) => {
    try {
      const { data } = await api.get("/admin/recovery/send-logs", {
        params: { tenant_slug: tenantSlug, enrollment_id: enrollmentId, limit: 50 },
      });
      setLogs((prev) => ({ ...prev, [enrollmentId]: Array.isArray(data) ? data : [] }));
    } catch { setLogs((prev) => ({ ...prev, [enrollmentId]: [] })); }
  };
  const expand = (id) => {
    setExpanded((cur) => cur === id ? null : id);
    if (id !== expanded && !logs[id]) loadLogsFor(id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap" data-testid="enrollment-filter">
          <FilterChip label={`All · ${rows.length}`} active={status === ""} onClick={() => setStatus("")} testId="enrollment-filter-all" />
          {ENROLLMENT_STATUSES.map((s) => (
            <FilterChip key={s} label={s} active={status === s}
              onClick={() => setStatus(s)} testId={`enrollment-filter-${s}`} />
          ))}
        </div>
        <button onClick={load} data-testid="enrollments-refresh"
          className="text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8] inline-flex items-center gap-1.5">
          <RefreshCw size={13} strokeWidth={1.8} /> Refresh
        </button>
      </div>
      <PanelState status={loadState.status} error={loadState.error}
        isEmpty={rows.length === 0} onRetry={load}
        emptyTitle="No enrollments yet"
        emptyBody="Enrollments appear here when a cancellation triggers an active sequence."
        testIdPrefix="enrollments" />
      {loadState.status === "ok" && rows.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-[#fafbfc] text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left px-4 py-2.5">Enrolled</th>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">Sequence</th>
                <th className="text-left px-4 py-2.5">Step</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Origin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <EnrollmentTableRows key={r.id} r={r}
                  expanded={expanded === r.id}
                  onClick={() => expand(r.id)}
                  logs={logs[r.id]} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Extract row + expanded-logs pair to a component so React sees ONE key
// per iteration instead of an unkeyed Fragment holding two children.
function EnrollmentTableRows({ r, expanded, onClick, logs }) {
  return (
    <>
      <tr data-testid={`enrollment-row-${r.id}`} onClick={onClick}
        className="border-t border-[#eef1f6] hover:bg-[#fafbfc] cursor-pointer">
        <td className="px-4 py-2.5 font-mono-data text-[11.5px]">{fmtDate(r.enrolled_at)}</td>
        <td className="px-4 py-2.5">{r.customer_ref || "—"}</td>
        <td className="px-4 py-2.5 font-mono-data text-[11.5px]">{r.sequence_id?.slice(0, 8) || "—"}</td>
        <td className="px-4 py-2.5 font-mono-data text-[11.5px]">{r.current_step ?? 0}</td>
        <td className="px-4 py-2.5">
          <span className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${ENROLLMENT_TONE[r.status] || ""}`}>
            {r.status}
          </span>
          {r.paused_reason && (
            <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1">
              {r.paused_reason}
            </div>
          )}
        </td>
        <td className="px-4 py-2.5 text-[11.5px]">
          {r.originated_as_reschedule ? "reschedule" : "cancellation"}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#fafbfc]" data-testid={`enrollment-logs-${r.id}`}>
          <td colSpan={6} className="px-4 py-3">
            <SendLogList logs={logs} />
          </td>
        </tr>
      )}
    </>
  );
}

function SendLogList({ logs }) {
  if (!logs) return <div className="text-[12px] text-[color:var(--cc-ink-muted)] italic">Loading logs…</div>;
  if (logs.length === 0) return <div className="text-[12px] text-[color:var(--cc-ink-muted)] italic">No sends recorded (nothing has fired for this enrollment yet).</div>;
  const STATUS_ICON = {
    sent: CheckCircle2, delivered: CheckCircle2, queued: Clock,
    failed: AlertCircle, bounced: AlertCircle, opted_out: PauseCircle,
    complained: AlertCircle, undelivered: AlertCircle,
  };
  const STATUS_COLOR = {
    sent: "#0e8a68", delivered: "#0e8a68", queued: "#8a5b00",
    failed: "#b3261e", bounced: "#b3261e", opted_out: "#4b5563",
    complained: "#b3261e", undelivered: "#b3261e",
  };
  return (
    <ul className="space-y-1.5">
      {logs.map((l) => {
        const Icon = STATUS_ICON[l.delivery_status] || AlertCircle;
        const color = STATUS_COLOR[l.delivery_status] || "#4b5563";
        return (
          <li key={l.id} className="flex items-start gap-2.5 text-[12px]"
            data-testid={`send-log-${l.id}`}>
            <Icon size={14} strokeWidth={1.8} style={{ color }} className="mt-0.5 shrink-0" />
            <div className="font-mono-data text-[color:var(--cc-ink-muted)] shrink-0">
              {fmtDate(l.sent_at || l.updated_at)}
            </div>
            <div className="uppercase tracking-widest text-[10.5px] font-mono-data" style={{ color }}>
              {l.delivery_status}
            </div>
            <div className="text-[color:var(--cc-ink-muted)] uppercase text-[10.5px] tracking-widest">{l.channel}</div>
            <div className="flex-1 min-w-0 truncate text-[#0e1420]">{l.recipient || "—"}</div>
            {l.mocked && (
              <span className="text-[10px] font-mono-data uppercase tracking-widest text-[#8a5b00] bg-[#fff4d6] border border-[#f3d97a] rounded px-1.5 py-0.5">
                mock
              </span>
            )}
            {l.error && (
              <div className="text-[10.5px] font-mono-data text-[#b3261e]" title={l.error}>
                {l.error.slice(0, 40)}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ============================================================================
// Reusable form primitives
// ============================================================================
function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">
        {label}{required && " *"}
      </div>
      {children}
    </label>
  );
}
function ErrorInline({ err, testId }) {
  return (
    <div data-testid={testId} className="bg-[#fff5f5] border border-[#f5c2c2] rounded-lg p-3 text-[12.5px] text-[#7a1c1c]">
      {err.message || "Something went wrong."}
    </div>
  );
}
function SubmitButtons({ busy, onClose, testIdPrefix, label }) {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <button type="button" onClick={onClose} disabled={busy}
        data-testid={`${testIdPrefix}-cancel`}
        className="text-[12.5px] font-semibold px-4 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8] disabled:opacity-40">
        Cancel
      </button>
      <button type="submit" disabled={busy}
        data-testid={`${testIdPrefix}-submit`}
        className="cc-btn-primary text-[12.5px] py-2 px-4 disabled:opacity-60">
        {busy ? "Saving…" : label}
      </button>
    </div>
  );
}
function ModalShell({ title, onClose, testId, children }) {
  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#0e1420]">{title}</div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[#f5f6f8]"
            data-testid={`${testId}-close`}>
            <X size={16} strokeWidth={1.6} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function FilterChip({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
        active
          ? "bg-[#0072bc] text-white border-[#0072bc]"
          : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
      }`}>
      {label}
    </button>
  );
}
